import type {
  PersistedClient,
  Persister,
} from '@tanstack/query-persist-client-core';

const DATABASE_NAME = 'commoncal-offline';
const STORE_NAME = 'query-cache';
const DATABASE_VERSION = 1;
const CACHE_KEY_PREFIX = 'user:';

function openDatabase(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DATABASE_NAME, DATABASE_VERSION);

    request.onupgradeneeded = () => {
      if (!request.result.objectStoreNames.contains(STORE_NAME)) {
        request.result.createObjectStore(STORE_NAME);
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

async function withStore<T>(
  mode: IDBTransactionMode,
  action: (store: IDBObjectStore) => IDBRequest<T>
): Promise<T> {
  const database = await openDatabase();
  try {
    return await new Promise<T>((resolve, reject) => {
      const transaction = database.transaction(STORE_NAME, mode);
      const request = action(transaction.objectStore(STORE_NAME));
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
      transaction.onerror = () => reject(transaction.error);
    });
  } finally {
    database.close();
  }
}

function cacheKey(userId: string): string {
  return `${CACHE_KEY_PREFIX}${userId}`;
}

export function createOfflineQueryPersister(userId: string): Persister {
  const key = cacheKey(userId);

  return {
    persistClient: persistedClient =>
      withStore('readwrite', store =>
        store.put(persistedClient, key)
      ).then(() => undefined),
    restoreClient: () =>
      withStore<PersistedClient | undefined>('readonly', store =>
        store.get(key)
      ),
    removeClient: () =>
      withStore('readwrite', store => store.delete(key)).then(
        () => undefined
      ),
  };
}

export async function removeOfflineQueryCache(
  userId: string
): Promise<void> {
  await createOfflineQueryPersister(userId).removeClient();
}
